import moment from 'moment';
import { Campaign } from 'entities/campaign';
import { RulesetSplitTest, Variant } from 'entities/rulesetSplitTest';
import { OnsiteTypes, Template } from 'entities/template';

interface Failure {
  isValid: false;
  errorMessage: string;
}

interface Success {
  isValid: true;
}

export function isSuccessfulValidation(obj: SplitTestValidationResponse): obj is Success {
  return obj.isValid;
}

type SplitTestValidationResponse = Success | Failure;

const minNameLength = 5;
const maxNameLength = 100;
const momentFormatString = 'D MMM YYYY HH:mm';

// SETTINGS SPECIFIC VALIDATION STEPS
export function validateSplitTestName(
  splitTest: RulesetSplitTest,
  otherSplitTests: RulesetSplitTest[],
): SplitTestValidationResponse {
  const isDuplicate = !!otherSplitTests && otherSplitTests.some(test => test.name === splitTest.name);

  if (!splitTest.name) {
    return {
      isValid: false,
      errorMessage: undefined,
    };
  }

  if (splitTest.name.length < minNameLength || splitTest.name.length > maxNameLength) {
    return {
      isValid: false,
      errorMessage: 'The split test name must be between 5 and 100 characters long',
    };
  }

  if (!/\w+/g.test(splitTest.name)) {
    return {
      isValid: false,
      errorMessage: 'The split test name must contain at least one non-whitespace character',
    };
  }

  if (isDuplicate) {
    return {
      isValid: false,
      errorMessage: `The name "${splitTest.name}" is already in use`,
    };
  }

  return { isValid: true };
}

export function validateSplitTestCampaignId(splitTest: RulesetSplitTest): SplitTestValidationResponse {
  if (!splitTest.campaignId) {
    return {
      isValid: false,
      errorMessage: undefined,
    };
  }

  return { isValid: true };
}

export function validateSplitTestRulesetId(splitTest: RulesetSplitTest): SplitTestValidationResponse {
  if (!splitTest.rulesetId) {
    return {
      isValid: false,
      errorMessage: undefined,
    };
  }

  return { isValid: true };
}

export function validateSplitTestStartDate(
  splitTest: RulesetSplitTest,
  campaign: Campaign,
): SplitTestValidationResponse {
  if (!splitTest.startTime) {
    return {
      isValid: false,
      errorMessage: undefined,
    };
  }

  if (splitTest.startTime < Date.now()) {
    return {
      isValid: false,
      errorMessage: 'The split tests start time cannot be in the past',
    };
  }

  if (!!campaign && splitTest.startTime < campaign.startTime) {
    return {
      isValid: false,
      errorMessage: `The start time of the split test cannot be before the campaign has begun. The campaign is scheduled to start at ${moment(
        campaign.startTime,
      ).format(momentFormatString)}`,
    };
  }

  return {
    isValid: true,
  };
}

export function validateSplitTestEndDate(splitTest: RulesetSplitTest, campaign: Campaign): SplitTestValidationResponse {
  const sevenDaysInMilliseconds = 604800000;

  if (!splitTest.endTime) {
    return {
      isValid: false,
      errorMessage: undefined,
    };
  }

  if (splitTest.endTime < splitTest.startTime || splitTest.endTime - splitTest.startTime < sevenDaysInMilliseconds) {
    return {
      isValid: false,
      errorMessage: 'The split tests end time must be at least 7 days after the split test start time',
    };
  }

  if (!!campaign && splitTest.endTime > campaign.endTime) {
    return {
      isValid: false,
      errorMessage: `The end time of the split test cannot be after the campaign has finished. The campaign is scheduled to finish at ${moment(
        campaign.endTime,
      ).format(momentFormatString)}`,
    };
  }

  return {
    isValid: true,
  };
}

export function validateSplitTestObjective(splitTest: RulesetSplitTest): SplitTestValidationResponse {
  if (!!splitTest.objective && splitTest.objective.valueOf() !== '') {
    return { isValid: true };
  }

  return {
    isValid: false,
    errorMessage: 'The split test has to have a valid objective selected',
  };
}

export function validateSplitTestType(splitTest: RulesetSplitTest): SplitTestValidationResponse {
  if (!!splitTest.type && splitTest.type.valueOf() !== '') {
    return { isValid: true };
  }

  return {
    isValid: false,
    errorMessage: 'The split test has to have a valid type selected',
  };
}

// VARIANT SPECIFIC VALIDATION STEPS
export function validateVariantAudienceShare(variant: Variant): SplitTestValidationResponse {
  if (variant.weight > 0 && variant.weight < 100) {
    return { isValid: true };
  }

  return {
    isValid: false,
    errorMessage: 'The variant weight must be between 1 and 100',
  };
}

export function validateVariantTemplate(variant: Variant): SplitTestValidationResponse {
  if (!variant.templateId) {
    return {
      isValid: false,
      errorMessage: undefined,
    };
  }

  return { isValid: true };
}

export function validateVariantSelector(variant: Variant): SplitTestValidationResponse {
  if (!variant.templateSelector) {
    return {
      isValid: false,
      errorMessage: undefined,
    };
  }

  return { isValid: true };
}

export function validateVariantPosition(variant: Variant): SplitTestValidationResponse {
  if (!variant.templatePosition) {
    return {
      isValid: false,
      errorMessage: undefined,
    };
  }

  return { isValid: true };
}

export function validateSettings(
  splitTest: RulesetSplitTest,
  campaign: Campaign,
  otherSiteSplitTests: RulesetSplitTest[],
): boolean {
  const validities: SplitTestValidationResponse[] = [
    validateSplitTestName(splitTest, otherSiteSplitTests),
    validateSplitTestCampaignId(splitTest),
    validateSplitTestRulesetId(splitTest),
    validateSplitTestStartDate(splitTest, campaign),
    validateSplitTestEndDate(splitTest, campaign),
    validateSplitTestObjective(splitTest),
    validateSplitTestType(splitTest),
  ];

  return validities.every(status => status.isValid);
}

export function validateVariantChoice(splitTest: RulesetSplitTest, templates: Template[]): boolean {
  const isValidAudienceShare =
    splitTest.variants.map(variant => variant.weight).reduce((acc, val) => acc + val, 0) < 100;
  const injectedVariantsHaveRequiredProps = splitTest.variants.every(variant => {
    const selectedTemplateObject = templates.find(tmpl => tmpl.id === variant.templateId);

    if (!selectedTemplateObject) {
      return false;
    }

    return (
      selectedTemplateObject.type === OnsiteTypes.Message ||
      (selectedTemplateObject.type === OnsiteTypes.Injected &&
        validateVariantSelector(variant).isValid &&
        validateVariantPosition(variant).isValid)
    );
  });

  return isValidAudienceShare && injectedVariantsHaveRequiredProps;
}
